<template>
    <div class="min-h-screen bg-gray-100 p-6">
        <div class="max-w-7xl mx-auto">
            <!-- Header -->
            <div class="bg-white rounded-lg shadow-md p-6 mb-6">
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-800">Commandes en Ligne</h1>
                        <p class="text-gray-600">{{ currentRestaurant.name }}</p>
                    </div>
                    <div class="flex space-x-4">
                        <router-link to="/" class="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
                            Retour
                        </router-link>
                        <Notifications />
                    </div>
                </div>

                <!-- Stats -->
                <div class="grid grid-cols-4 gap-4 mt-6">
                    <div class="bg-blue-50 p-4 rounded-lg">
                        <div class="text-2xl font-bold text-blue-600">{{ pendingOrders.length }}</div>
                        <div class="text-sm text-blue-800">En attente</div>
                    </div>
                    <div class="bg-yellow-50 p-4 rounded-lg">
                        <div class="text-2xl font-bold text-yellow-600">{{ preparingOrders.length }}</div>
                        <div class="text-sm text-yellow-800">En préparation</div>
                    </div>
                    <div class="bg-orange-50 p-4 rounded-lg">
                        <div class="text-2xl font-bold text-orange-600">{{ readyOrders.length }}</div>
                        <div class="text-sm text-orange-800">Prêtes</div>
                    </div>
                    <div class="bg-green-50 p-4 rounded-lg">
                        <div class="text-2xl font-bold text-green-600">{{ deliveredOrders.length }}</div>
                        <div class="text-sm text-green-800">Livrées</div>
                    </div>
                </div>
            </div>

            <!-- Orders Grid -->
            <div class="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                <!-- Pending Orders -->
                <div class="bg-white rounded-lg shadow-md">
                    <div class="bg-yellow-500 text-white p-4 rounded-t-lg">
                        <h2 class="text-xl font-semibold">En Attente</h2>
                    </div>
                    <div class="p-4 space-y-4 max-h-96 overflow-y-auto">
                        <div v-for="order in pendingOrders" :key="order.id"
                             class="border border-yellow-200 rounded-lg p-4">
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">#{{ order.orderNumber }}</p>
                                    <p class="text-sm text-gray-600">{{ order.customer.firstName }} {{ order.customer.lastName }}</p>
                                    <p class="text-xs text-gray-500">{{ order.type === 'delivery' ? 'Livraison' : 'À emporter' }}</p>
                                </div>
                                <span class="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                  {{ order.total }}€
                </span>
                            </div>

                            <div class="text-xs text-gray-600 mb-3">
                                <div v-for="item in order.items" :key="item.id">
                                    {{ item.quantity }}x {{ item.name }}
                                </div>
                            </div>

                            <div class="flex space-x-2">
                                <button @click="acceptOrder(order.id)"
                                        class="flex-1 bg-green-600 text-white py-1 px-2 rounded text-sm hover:bg-green-700">
                                    Accepter
                                </button>
                                <button @click="rejectOrder(order.id)"
                                        class="flex-1 bg-red-600 text-white py-1 px-2 rounded text-sm hover:bg-red-700">
                                    Refuser
                                </button>
                            </div>
                        </div>

                        <div v-if="pendingOrders.length === 0" class="text-center text-gray-500 py-8">
                            Aucune commande en attente
                        </div>
                    </div>
                </div>

                <!-- Preparing Orders -->
                <div class="bg-white rounded-lg shadow-md">
                    <div class="bg-blue-500 text-white p-4 rounded-t-lg">
                        <h2 class="text-xl font-semibold">En Préparation</h2>
                    </div>
                    <div class="p-4 space-y-4 max-h-96 overflow-y-auto">
                        <div v-for="order in preparingOrders" :key="order.id"
                             class="border border-blue-200 rounded-lg p-4">
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">#{{ order.orderNumber }}</p>
                                    <p class="text-sm text-gray-600">{{ order.customer.firstName }} {{ order.customer.lastName }}</p>
                                    <p class="text-xs text-gray-500">
                                        {{ order.type === 'delivery' ? `Livraison: ${order.delivery.zone.name}` : 'À emporter' }}
                                    </p>
                                </div>
                                <span class="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  {{ order.total }}€
                </span>
                            </div>

                            <div class="text-xs text-gray-600 mb-3">
                                <div v-for="item in order.items" :key="item.id">
                                    {{ item.quantity }}x {{ item.name }}
                                </div>
                            </div>

                            <button @click="markAsReady(order.id)"
                                    class="w-full bg-orange-600 text-white py-2 rounded text-sm hover:bg-orange-700">
                                Marquer comme prête
                            </button>
                        </div>

                        <div v-if="preparingOrders.length === 0" class="text-center text-gray-500 py-8">
                            Aucune commande en préparation
                        </div>
                    </div>
                </div>

                <!-- Ready Orders -->
                <div class="bg-white rounded-lg shadow-md">
                    <div class="bg-green-500 text-white p-4 rounded-t-lg">
                        <h2 class="text-xl font-semibold">Prêtes</h2>
                    </div>
                    <div class="p-4 space-y-4 max-h-96 overflow-y-auto">
                        <div v-for="order in readyOrders" :key="order.id"
                             class="border border-green-200 rounded-lg p-4">
                            <div class="flex justify-between items-start mb-2">
                                <div>
                                    <p class="font-semibold">#{{ order.orderNumber }}</p>
                                    <p class="text-sm text-gray-600">{{ order.customer.firstName }} {{ order.customer.lastName }}</p>
                                    <p class="text-xs text-gray-500">
                                        {{ order.type === 'delivery' ? `Livraison: ${order.delivery.zone.name}` : 'À emporter' }}
                                    </p>
                                </div>
                                <span class="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  {{ order.total }}€
                </span>
                            </div>

                            <div class="text-xs text-gray-600 mb-3">
                                <div v-for="item in order.items" :key="item.id">
                                    {{ item.quantity }}x {{ item.name }}
                                </div>
                            </div>

                            <div v-if="order.type === 'delivery'" class="mb-3">
                                <label class="block text-xs text-gray-600 mb-1">Assigner un livreur:</label>
                                <select v-model="selectedDrivers[order.id]" class="w-full text-sm border rounded p-1">
                                    <option value="">Choisir un livreur</option>
                                    <option v-for="driver in availableDrivers" :key="driver.id" :value="driver.id">
                                        {{ driver.name }}
                                    </option>
                                </select>
                                <button @click="assignDriver(order.id)"
                                        class="w-full mt-2 bg-purple-600 text-white py-1 rounded text-sm hover:bg-purple-700">
                                    Assigner
                                </button>
                            </div>

                            <button @click="completeOrder(order.id)"
                                    class="w-full bg-green-600 text-white py-2 rounded text-sm hover:bg-green-700">
                                {{ order.type === 'delivery' ? 'Début livraison' : 'Marquer récupérée' }}
                            </button>
                        </div>

                        <div v-if="readyOrders.length === 0" class="text-center text-gray-500 py-8">
                            Aucune commande prête
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { mapState, mapGetters, mapActions } from 'vuex';
    import Notifications from '../notifications.vue';

    export default {
        name: 'OnlineOrdersDashboard',
        components: {
            Notifications
        },
        data() {
            return {
                selectedDrivers: {}
            };
        },
        computed: {
            ...mapState(['currentRestaurant']),
            ...mapState('onlineOrders', ['onlineOrders', 'deliveryDrivers']),
            ...mapGetters('onlineOrders', [
                'pendingOnlineOrders',
                'activeOnlineOrders',
                'availableDrivers'
            ]),
            pendingOrders() {
                return this.pendingOnlineOrders;
            },
            preparingOrders() {
                return this.onlineOrders.filter(order =>
                    ['accepted', 'preparing'].includes(order.status)
                );
            },
            readyOrders() {
                return this.onlineOrders.filter(order =>
                    ['ready_pickup', 'assigned'].includes(order.status)
                );
            },
            deliveredOrders() {
                return this.onlineOrders.filter(order =>
                    ['delivered', 'picked_up'].includes(order.status)
                );
            }
        },
        methods: {
            ...mapActions('onlineOrders', [
                'acceptOnlineOrder',
                'rejectOnlineOrder',
                'readyForPickup',
                'assignDriver',
                'startDelivery',
                'completeDelivery'
            ]),

            async acceptOrder(orderId) {
                if (confirm('Accepter cette commande ?')) {
                    await this.acceptOnlineOrder(orderId);
                }
            },

            async rejectOrder(orderId) {
                if (confirm('Refuser cette commande ?')) {
                    await this.rejectOnlineOrder(orderId);
                }
            },

            async markAsReady(orderId) {
                await this.readyForPickup(orderId);
            },

            async assignDriver(orderId) {
                const driverId = this.selectedDrivers[orderId];
                if (driverId) {
                    await this.assignDriver({ orderId, driverId });
                    delete this.selectedDrivers[orderId];
                }
            },

            async completeOrder(orderId) {
                const order = this.onlineOrders.find(o => o.id === orderId);
                if (order.type === 'delivery') {
                    await this.startDelivery(orderId);
                } else {
                    await this.completeDelivery(orderId);
                }
            }
        }
    };
</script>